// This module assembles the route tree and exports router factory/types
// alongside route components, so fast-refresh's single-export rule does not
// apply. It is a configuration entry point, not a hot-reloaded component file.
/* oxlint-disable react/only-export-components */

// 1. React and core hooks
import { lazy, Suspense } from 'react';

// 2. External libraries
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  HeadContent,
  Outlet,
  useRouteContext,
} from '@tanstack/react-router';
import { z } from 'zod';

// 3. Internal components
import { onboardingBeforeLoad } from '~/components/auth/OnboardingRouteGuard';
import { requireSignedIn } from '~/components/auth/RedirectToSignInWithReturn';
import { AuthTokenBridge } from '~/components/auth/AuthTokenBridge';
import { ErrorAlert } from '~/components/common/ErrorAlert';
import { ErrorBoundary } from '~/components/common/ErrorBoundary';
import { FullPageSpinner } from '~/components/common/FullPageSpinner';
import { WebSocketProvider } from '~/providers/WebSocketProvider';
import { RootLayout } from '~/routes/RootLayout';
import { SignInPage } from '~/routes/SignInPage';
import { SignOutPage } from '~/routes/SignOutPage';
import { SsoCallbackPage } from '~/routes/SsoCallbackPage';
import { MagicLinkCallbackPage } from '~/routes/MagicLinkCallbackPage';
import { AuthContinuePage } from '~/routes/AuthContinuePage';
import { AuthErrorPage } from '~/routes/AuthErrorPage';
import { OnboardingPage } from '~/routes/OnboardingPage';
import { TelegramDemoPage } from '~/routes/TelegramDemoPage';
import { OAuthAuthorizePage } from '~/routes/OAuthAuthorizePage';
import { SlackOAuthCallbackPage } from '~/routes/SlackOAuthCallbackPage';
import { DiscordOAuthCallbackPage } from '~/routes/DiscordOAuthCallbackPage';

// 4. Types
import type { UseAuthReturn } from '~/hooks/useAuth';
import type { QueryClient } from '@tanstack/react-query';
import type { ErrorComponentProps } from '@tanstack/react-router';
import type { CreateConnectionResponse } from '~/generated/api/model';

// Lazy-load feature routes so their chunks split out of the initial bundle. The
// route `pendingComponent` covers loader-pending states; the Suspense fallback
// covers chunk download, both rendering the shared full-page spinner.
const TopicsPage = lazy(() =>
  import('~/routes/TopicsPage').then((module) => ({ default: module.TopicsPage }))
);
const TopicDetailPage = lazy(() =>
  import('~/routes/TopicDetailPage').then((module) => ({ default: module.TopicDetailPage }))
);
const ConnectionDetailPage = lazy(() =>
  import('~/routes/ConnectionDetailPage').then((module) => ({
    default: module.ConnectionDetailPage,
  }))
);
const ProfilePage = lazy(() =>
  import('~/routes/ProfilePage').then((module) => ({ default: module.ProfilePage }))
);
const NotificationsPage = lazy(() =>
  import('~/routes/NotificationsPage').then((module) => ({ default: module.NotificationsPage }))
);
const TelegramConnectPage = lazy(() =>
  import('~/routes/TelegramConnectPage').then((module) => ({
    default: module.TelegramConnectPage,
  }))
);

/**
 * Dependencies the route tree resolves through the router context rather than
 * React context, so loaders and route components read a stable, typed handle.
 *
 * `auth` carries the current Clerk-derived session so route guards branch on
 * sign-in state. `queryClient` lets loaders prefetch and read the shared cache.
 * Theme controls flow through here so the root layout toggles dark/light mode
 * without prop drilling once routes migrate off the legacy router.
 */
export interface RouterContext {
  auth: UseAuthReturn;
  queryClient: QueryClient;
  darkMode: boolean;
  toggleDarkMode: () => void;
}

// --- Search param schemas ---
//
// Every schema is intentionally permissive: each field is optional and the OAuth
// schemas accept arbitrary extra string params via `.catchall`. Auth providers
// append provider-specific query params to redirect URLs, so a strict schema
// would reject valid callbacks. Validation here only types the params each page
// reads; it never gates the redirect.

const signInSearchSchema = z.object({
  returnUrl: z.string().optional(),
  redirect_url: z.string().optional(),
  reason: z.string().optional().catch(undefined),
});

const ssoCallbackSearchSchema = z.object({
  redirect_url: z.string().optional(),
  failed: z.string().optional().catch(undefined),
});

const magicLinkCallbackSearchSchema = z.object({
  redirect_url: z.string().optional(),
});

const authErrorSearchSchema = z.object({
  type: z.string().optional(),
});

const oauthAuthorizeSearchSchema = z
  .object({
    client_id: z.string().optional(),
    redirect_uri: z.string().optional(),
    response_type: z.string().optional(),
    scope: z.string().optional(),
    state: z.string().optional(),
    code_challenge: z.string().optional(),
    code_challenge_method: z.string().optional(),
  })
  .catchall(z.string());

const slackOAuthCallbackSearchSchema = z
  .object({
    connectionId: z.string().optional(),
    topicId: z.string().optional(),
    error: z.string().optional(),
    message: z.string().optional(),
  })
  .catchall(z.string());

const discordOAuthCallbackSearchSchema = z
  .object({
    connectionId: z.string().optional(),
    topicId: z.string().optional(),
    error: z.string().optional(),
    message: z.string().optional(),
  })
  .catchall(z.string());

const telegramConnectSearchSchema = z.object({
  token: z.string().optional(),
});

/**
 * Shared per-route error boundary. TanStack calls this with the thrown error and
 * a `reset` callback; it renders the standard error alert with a retry action so
 * every route satisfies the per-route error-boundary requirement.
 */
function RouteErrorComponent({ error, reset }: ErrorComponentProps) {
  const message =
    error instanceof Error && error.message ? error.message : 'An unexpected error occurred';
  return <ErrorAlert message={message} onRetry={reset} />;
}

/**
 * Wraps a lazily-loaded route component in Suspense so the chunk download shows
 * the shared full-page spinner instead of a blank frame.
 */
function lazyRouteComponent(Component: React.ComponentType) {
  return function LazyRoute() {
    return (
      <Suspense fallback={<FullPageSpinner />}>
        <Component />
      </Suspense>
    );
  };
}

/**
 * Root route renders the application shell: the auth-token bridge that keeps the
 * HTTP client's Clerk token current, a top-level error backstop, the global
 * WebSocket provider, and the child route outlet. The WebSocket provider lives
 * here, inside the router tree, because `useWebSocket` reads `useLocation()` to
 * skip connecting on exempt routes; that hook needs the router context, so the
 * provider must render below `RouterProvider`. The pending fallback reuses the
 * shared full-page spinner.
 */
const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootRoute,
  pendingComponent: FullPageSpinner,
  head: () => ({ meta: [{ title: 'DeadDrop' }] }),
});

function RootRoute() {
  return (
    <>
      <HeadContent />
      <AuthTokenBridge />
      <ErrorBoundary>
        <WebSocketProvider>
          <Outlet />
        </WebSocketProvider>
      </ErrorBoundary>
    </>
  );
}

// Every leaf route below sets a per-page <title> via `head`, rendered through
// the root route's <HeadContent />.
function pageHead(title: string) {
  return () => ({ meta: [{ title: `${title} — DeadDrop` }] });
}

// --- Public routes (no guard) ---

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: signInSearchSchema,
  component: SignInPage,
  head: pageHead('Sign in'),
});

const signOutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-out',
  component: SignOutPage,
  head: pageHead('Sign out'),
});

const ssoCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sso-callback',
  validateSearch: ssoCallbackSearchSchema,
  component: SsoCallbackPage,
  head: pageHead('Signing in'),
});

const magicLinkCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/magic-link-callback',
  validateSearch: magicLinkCallbackSearchSchema,
  component: MagicLinkCallbackPage,
  head: pageHead('Signing in'),
});

const authErrorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth-error',
  validateSearch: authErrorSearchSchema,
  component: AuthErrorPage,
  head: pageHead('Sign-in error'),
});

const authContinueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth-continue',
  component: AuthContinuePage,
  head: pageHead('Verification required'),
});

// --- Signed-in-gated standalone routes (sign-in guard only) ---

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  beforeLoad: requireSignedIn,
  component: OnboardingPage,
  head: pageHead('Get started'),
});

const telegramDemoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/telegram-demo',
  beforeLoad: requireSignedIn,
  component: TelegramDemoPage,
  head: pageHead('Telegram demo'),
});

const oauthAuthorizeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth/authorize',
  validateSearch: oauthAuthorizeSearchSchema,
  beforeLoad: requireSignedIn,
  component: OAuthAuthorizePage,
  head: pageHead('Authorize app'),
});

const slackOAuthCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth/slack/callback',
  validateSearch: slackOAuthCallbackSearchSchema,
  beforeLoad: requireSignedIn,
  component: SlackOAuthCallbackPage,
  head: pageHead('Connecting Slack'),
});

const discordOAuthCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth/discord/callback',
  validateSearch: discordOAuthCallbackSearchSchema,
  beforeLoad: requireSignedIn,
  component: DiscordOAuthCallbackPage,
  head: pageHead('Connecting Discord'),
});

// Telegram connect is a top-level route to ensure proper auth redirect handling.
const telegramConnectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/telegram/connect',
  validateSearch: telegramConnectSearchSchema,
  beforeLoad: requireSignedIn,
  component: lazyRouteComponent(TelegramConnectPage),
  head: pageHead('Connect Telegram'),
});

// --- Authenticated layout subtree (sign-in AND onboarding guards) ---

/**
 * Layout route for the authenticated application shell. Runs both the sign-in
 * and onboarding guards before loading, then renders `RootLayout` with the child
 * outlet. Theme controls come from the router context rather than props.
 */
const authenticatedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: async (opts) => {
    requireSignedIn(opts);
    await onboardingBeforeLoad(opts);
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { darkMode, toggleDarkMode } = useRouteContext({ from: authenticatedLayoutRoute.id });
  return <RootLayout darkMode={darkMode} toggleDarkMode={toggleDarkMode} />;
}

const topicsIndexRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/',
  component: lazyRouteComponent(TopicsPage),
  head: pageHead('Topics'),
});

const profileRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: 'profile',
  component: lazyRouteComponent(ProfilePage),
  head: pageHead('Profile'),
});

const notificationsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: 'notifications',
  component: lazyRouteComponent(NotificationsPage),
  head: pageHead('Notifications'),
});

const topicDetailRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: 'topics/$topicId',
  component: lazyRouteComponent(TopicDetailPage),
  head: pageHead('Topic'),
});

const connectionDetailRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: 'topics/$topicId/connections/$connectionId',
  component: lazyRouteComponent(ConnectionDetailPage),
  head: pageHead('Connection'),
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  signOutRoute,
  ssoCallbackRoute,
  magicLinkCallbackRoute,
  authErrorRoute,
  authContinueRoute,
  onboardingRoute,
  telegramDemoRoute,
  oauthAuthorizeRoute,
  slackOAuthCallbackRoute,
  discordOAuthCallbackRoute,
  telegramConnectRoute,
  authenticatedLayoutRoute.addChildren([
    topicsIndexRoute,
    profileRoute,
    notificationsRoute,
    topicDetailRoute,
    connectionDetailRoute,
  ]),
]);

/**
 * Creates the application router. The caller supplies the runtime context
 * (`auth`, `queryClient`, theme controls) through `RouterProvider`, so the
 * context here only declares the type-level shape via `undefined!`.
 */
export function createAppRouter() {
  return createRouter({
    routeTree,
    context: undefined!,
    defaultPendingComponent: FullPageSpinner,
    defaultErrorComponent: RouteErrorComponent,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

// Register the router instance type so `Link`, `useNavigate`, and route hooks
// infer the full route tree without per-call generics.
declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}

// Augment the router's typed history state so `navigate({ state })` accepts the
// custom keys our flows pass between routes. TanStack types `state` against this
// interface; without these declarations the typed navigate rejects the payloads.
//
// `newConnection` carries the freshly created connection (including the one-time
// apiKey/secret) to the detail page for the post-creation reveal.
// `oauthComplete` signals the detail page that an OAuth install just finished.
// `reopenConnectionDialog` tells the topic page to reopen the create dialog
// after a failed OAuth install.
declare module '@tanstack/history' {
  interface HistoryState {
    newConnection?: CreateConnectionResponse;
    oauthComplete?: boolean;
    reopenConnectionDialog?: boolean;
  }
}
